import { useEffect, useState } from "react";
import { ShieldCheck, FileText, Bot, Building2, UserCheck, AlertTriangle, Plus, Trash2, Check, Upload, ChevronDown, X } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import {
  tpDashboard, tpReminders,
  tpQuestionnaires, tpQuestionnaireCreate, tpQuestionnaireGet, tpQuestionnairePatch, tpQuestionnaireAnswer,
  tpAiSystems, tpAiSystemCreate, tpAiSystemPatch, tpAiSystemDelete,
  tpVendors, tpVendorCreate, tpVendorPatch, tpVendorDelete,
  tpAccessReviews, tpAccessReviewCreate, tpAccessReviewDecide, tpAccessReviewComplete,
  tpEvidenceUpload, tpEvidenceList,
} from "@/lib/api";

const TABS = [
  { key: "overview", label: "Overview", icon: ShieldCheck },
  { key: "questionnaires", label: "Questionnaires", icon: FileText },
  { key: "ai_systems", label: "AI Governance", icon: Bot },
  { key: "vendors", label: "Vendors", icon: Building2 },
  { key: "access_reviews", label: "Access Reviews", icon: UserCheck },
];

const RISK_COLORS = {
  low: "bg-emerald-100 text-emerald-800 border-emerald-200",
  medium: "bg-amber-100 text-amber-800 border-amber-200",
  high: "bg-orange-100 text-orange-800 border-orange-200",
  critical: "bg-red-100 text-red-800 border-red-200",
};

export default function TrustPhase2() {
  const [tab, setTab] = useState("overview");
  useEffect(() => { document.title = "Trust & Compliance | WebsiteTech.pro"; }, []);
  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <Navbar />
      <main className="pt-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex-1 w-full" data-testid="trust-phase2">
        <header className="mb-8">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-indigo-700 font-bold">
            <ShieldCheck className="w-3.5 h-3.5" /> Trust & Compliance
          </div>
          <h1 className="mt-2 text-3xl md:text-4xl font-bold text-slate-900">
            Registers, reviews and evidence — one control plane.
          </h1>
          <p className="mt-2 text-slate-600 max-w-2xl">
            Track your AI systems, vendors, due-diligence questionnaires and periodic access reviews. Attach evidence. Get reminders before anything expires.
          </p>
        </header>

        <nav className="flex flex-wrap gap-2 mb-6" role="tablist">
          {TABS.map((t) => {
            const active = tab === t.key;
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                data-testid={`tp-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border transition ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}
              >
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </nav>

        {tab === "overview" && <TabOverview />}
        {tab === "questionnaires" && <TabQuestionnaires />}
        {tab === "ai_systems" && <TabAiSystems />}
        {tab === "vendors" && <TabVendors />}
        {tab === "access_reviews" && <TabAccessReviews />}
      </main>
      <Footer />
    </div>
  );
}

/* ------------------------------------------------------------------ Overview */
function TabOverview() {
  const [dash, setDash] = useState(null);
  const [rem, setRem] = useState(null);
  useEffect(() => {
    tpDashboard().then(setDash).catch(() => {});
    tpReminders(30).then(setRem).catch(() => {});
  }, []);
  const cards = dash ? [
    { label: "Questionnaires", value: dash.totals.questionnaires, icon: FileText, color: "indigo" },
    { label: "AI systems", value: dash.totals.ai_systems, icon: Bot, color: "cyan" },
    { label: "Vendors", value: dash.totals.vendors, icon: Building2, color: "emerald" },
    { label: "Access reviews", value: dash.totals.access_reviews, icon: UserCheck, color: "amber" },
  ] : [];
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm" data-testid={`tp-card-${c.label.split(' ')[0].toLowerCase()}`}>
            <c.icon className="w-5 h-5 text-slate-500" />
            <div className="mt-3 text-3xl font-extrabold text-slate-900">{c.value}</div>
            <div className="text-xs uppercase tracking-wider text-slate-500 mt-1">{c.label}</div>
          </div>
        ))}
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {["ai_systems", "vendors", "access_reviews"].map((k) => (
          <div key={k} className="bg-white rounded-2xl border border-slate-200 p-5">
            <div className="text-sm font-semibold text-slate-900 flex items-center gap-2 mb-3">
              <AlertTriangle className="w-4 h-4 text-amber-500" /> Due in 30 days: {k.replaceAll("_", " ")}
            </div>
            <div className="space-y-2">
              {(rem?.[k] || []).slice(0, 5).map((r) => (
                <div key={r.id} className="text-xs bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                  <div className="font-semibold text-slate-800">{r.name || r.title}</div>
                  <div className="text-slate-500 mt-0.5">
                    {r.next_review_at ? `Review ${new Date(r.next_review_at).toLocaleDateString()}` : ""}
                    {r.due_at ? `Due ${new Date(r.due_at).toLocaleDateString()}` : ""}
                  </div>
                </div>
              ))}
              {!rem?.[k]?.length && <div className="text-xs text-slate-500">Nothing due — nice work.</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------- Shared */
function Empty({ text }) {
  return <div className="bg-white rounded-2xl border border-dashed border-slate-300 py-14 text-center text-sm text-slate-500">{text}</div>;
}
function RiskPill({ risk }) {
  return <span className={`inline-block text-[10px] uppercase tracking-widest font-bold border rounded-full px-2 py-0.5 ${RISK_COLORS[risk] || RISK_COLORS.medium}`}>{risk}</span>;
}

/* ------------------------------------------------------------ Questionnaires */
function TabQuestionnaires() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", framework: "custom", description: "", questions: "" });
  const reload = () => tpQuestionnaires().then((d) => setItems(d.items || []));
  useEffect(() => { reload(); }, []);
  const create = async () => {
    const questions = (form.questions || "").split("\n").map((q) => q.trim()).filter(Boolean).map((prompt) => ({ prompt, kind: "short_text" }));
    await tpQuestionnaireCreate({ ...form, questions });
    setOpen(false); setForm({ title: "", framework: "custom", description: "", questions: "" });
    reload();
  };
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-sm text-slate-500">{items.length} questionnaires</div>
        <button data-testid="tp-q-new" onClick={() => setOpen(true)} className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold rounded-lg px-4 py-2 transition"><Plus size={14} /> New questionnaire</button>
      </div>
      {items.length === 0 ? <Empty text="No questionnaires yet — click New to create your first SOC2 / DPA / DDQ." /> : (
        <div className="grid md:grid-cols-2 gap-3">
          {items.map((q) => (
            <div key={q.id} className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`tp-q-${q.id.slice(0,6)}`}>
              <div className="flex items-center justify-between">
                <div className="font-semibold text-slate-900">{q.title}</div>
                <span className="text-[10px] uppercase tracking-widest font-bold border rounded-full px-2 py-0.5 bg-slate-100 text-slate-700 border-slate-200">{q.status}</span>
              </div>
              <div className="text-xs text-slate-500 mt-1">{q.framework} · {q.questions?.length || 0} questions · review by {q.review_due_at ? new Date(q.review_due_at).toLocaleDateString() : "—"}</div>
              {q.description && <div className="text-sm text-slate-700 mt-2 line-clamp-2">{q.description}</div>}
              <div className="mt-3 flex gap-2">
                <button data-testid={`tp-q-approve-${q.id.slice(0,6)}`} onClick={async () => { await tpQuestionnairePatch(q.id, { status: "approved" }); reload(); }} className="text-xs px-3 py-1 rounded-md bg-emerald-100 text-emerald-800 hover:bg-emerald-200 font-semibold">Approve</button>
                <button onClick={async () => { await tpQuestionnairePatch(q.id, { status: "in_review" }); reload(); }} className="text-xs px-3 py-1 rounded-md bg-amber-100 text-amber-800 hover:bg-amber-200 font-semibold">In review</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {open && (
        <Modal onClose={() => setOpen(false)} title="New questionnaire">
          <div className="space-y-3">
            <input data-testid="tp-q-title" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <select className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" value={form.framework} onChange={(e) => setForm({ ...form, framework: e.target.value })}>
              {["custom", "soc2", "iso27001", "gdpr", "dpa", "hipaa"].map((f) => <option key={f}>{f}</option>)}
            </select>
            <textarea className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm min-h-[80px]" placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            <textarea className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm min-h-[120px]" placeholder="One question per line" value={form.questions} onChange={(e) => setForm({ ...form, questions: e.target.value })} />
            <button data-testid="tp-q-create" onClick={create} disabled={!form.title} className="w-full bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white font-semibold rounded-lg px-4 py-2.5">Create</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- AI Systems */
function TabAiSystems() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", purpose: "", model_or_vendor: "", risk: "medium", data_sensitivity: "internal" });
  const reload = () => tpAiSystems().then((d) => setItems(d.items || []));
  useEffect(() => { reload(); }, []);
  const create = async () => { await tpAiSystemCreate(form); setOpen(false); setForm({ name: "", purpose: "", model_or_vendor: "", risk: "medium", data_sensitivity: "internal" }); reload(); };
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-sm text-slate-500">{items.length} AI systems tracked</div>
        <button data-testid="tp-ai-new" onClick={() => setOpen(true)} className="inline-flex items-center gap-2 bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-semibold rounded-lg px-4 py-2 transition"><Plus size={14} /> Register AI system</button>
      </div>
      {items.length === 0 ? <Empty text="No AI systems yet — register the models and vendors your business uses." /> : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
              <tr><th className="text-left px-4 py-2">Name</th><th className="text-left px-4 py-2">Vendor</th><th className="text-left px-4 py-2">Sensitivity</th><th className="text-left px-4 py-2">Risk</th><th className="text-left px-4 py-2">Next review</th><th></th></tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.id} className="border-t border-slate-100" data-testid={`tp-ai-${a.id.slice(0,6)}`}>
                  <td className="px-4 py-3 font-semibold text-slate-900">{a.name}</td>
                  <td className="px-4 py-3 text-slate-600">{a.model_or_vendor}</td>
                  <td className="px-4 py-3 text-slate-600">{a.data_sensitivity}</td>
                  <td className="px-4 py-3"><RiskPill risk={a.risk} /></td>
                  <td className="px-4 py-3 text-slate-600 text-xs">{a.next_review_at ? new Date(a.next_review_at).toLocaleDateString() : "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={async () => { await tpAiSystemPatch(a.id, { mark_reviewed: true }); reload(); }} className="text-xs px-2 py-1 rounded bg-emerald-100 text-emerald-800 hover:bg-emerald-200 font-semibold mr-1">Mark reviewed</button>
                    <button onClick={async () => { if (confirm("Delete?")) { await tpAiSystemDelete(a.id); reload(); } }} className="text-slate-400 hover:text-red-600"><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {open && (
        <Modal onClose={() => setOpen(false)} title="Register AI system">
          <div className="space-y-3">
            <input data-testid="tp-ai-name" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" placeholder="Vendor / model (e.g. OpenAI gpt-5.4)" value={form.model_or_vendor} onChange={(e) => setForm({ ...form, model_or_vendor: e.target.value })} />
            <textarea className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm min-h-[80px]" placeholder="Purpose" value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} />
            <div className="grid grid-cols-2 gap-2">
              <select className="border border-slate-300 rounded-md px-3 py-2 text-sm" value={form.data_sensitivity} onChange={(e) => setForm({ ...form, data_sensitivity: e.target.value })}>
                {["public", "internal", "confidential", "regulated"].map((v) => <option key={v}>{v}</option>)}
              </select>
              <select className="border border-slate-300 rounded-md px-3 py-2 text-sm" value={form.risk} onChange={(e) => setForm({ ...form, risk: e.target.value })}>
                {["low", "medium", "high", "critical"].map((v) => <option key={v}>{v}</option>)}
              </select>
            </div>
            <button data-testid="tp-ai-create" onClick={create} disabled={!form.name} className="w-full bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 text-white font-semibold rounded-lg px-4 py-2.5">Register</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------- Vendors */
function TabVendors() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", category: "", website: "", data_processed: "", dpa_on_file: false, risk: "medium" });
  const reload = () => tpVendors().then((d) => setItems(d.items || []));
  useEffect(() => { reload(); }, []);
  const create = async () => { await tpVendorCreate(form); setOpen(false); setForm({ name: "", category: "", website: "", data_processed: "", dpa_on_file: false, risk: "medium" }); reload(); };
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-sm text-slate-500">{items.length} vendors</div>
        <button data-testid="tp-v-new" onClick={() => setOpen(true)} className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold rounded-lg px-4 py-2 transition"><Plus size={14} /> Add vendor</button>
      </div>
      {items.length === 0 ? <Empty text="No vendors yet — add the 3rd parties your business relies on." /> : (
        <div className="grid md:grid-cols-2 gap-3">
          {items.map((v) => (
            <div key={v.id} className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`tp-v-${v.id.slice(0,6)}`}>
              <div className="flex items-center justify-between">
                <div className="font-semibold text-slate-900">{v.name}</div>
                <RiskPill risk={v.risk} />
              </div>
              <div className="text-xs text-slate-500 mt-1">{v.category || "—"} · {v.dpa_on_file ? "DPA on file" : "No DPA on file"}</div>
              {v.website && <a href={v.website} target="_blank" rel="noreferrer" className="text-xs text-indigo-600 hover:underline mt-1 inline-block">{v.website}</a>}
              <div className="mt-2 text-sm text-slate-700 line-clamp-2">{v.data_processed || "—"}</div>
              <div className="mt-3 flex gap-2">
                <button onClick={async () => { await tpVendorPatch(v.id, { mark_reviewed: true }); reload(); }} className="text-xs px-3 py-1 rounded-md bg-emerald-100 text-emerald-800 hover:bg-emerald-200 font-semibold">Mark reviewed</button>
                <button onClick={async () => { if (confirm("Delete?")) { await tpVendorDelete(v.id); reload(); } }} className="text-slate-400 hover:text-red-600 ml-auto"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      {open && (
        <Modal onClose={() => setOpen(false)} title="Add vendor">
          <div className="space-y-3">
            <input data-testid="tp-v-name" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" placeholder="Vendor name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" placeholder="Category (payments, email, hosting…)" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
            <input className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" placeholder="Website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />
            <textarea className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm min-h-[80px]" placeholder="What data do they process?" value={form.data_processed} onChange={(e) => setForm({ ...form, data_processed: e.target.value })} />
            <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={form.dpa_on_file} onChange={(e) => setForm({ ...form, dpa_on_file: e.target.checked })} /> DPA signed on file</label>
            <select className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" value={form.risk} onChange={(e) => setForm({ ...form, risk: e.target.value })}>
              {["low", "medium", "high", "critical"].map((v) => <option key={v}>{v}</option>)}
            </select>
            <button data-testid="tp-v-create" onClick={create} disabled={!form.name} className="w-full bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-semibold rounded-lg px-4 py-2.5">Add vendor</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ------------------------------------------------------------ Access Reviews */
function TabAccessReviews() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", scope: "", items: "" });
  const reload = () => tpAccessReviews().then((d) => setItems(d.items || []));
  useEffect(() => { reload(); }, []);
  const create = async () => {
    const rows = (form.items || "").split("\n").map((line) => line.trim()).filter(Boolean).map((line) => {
      const [user, role] = line.split(",").map((s) => (s || "").trim());
      return { user, role: role || "read" };
    });
    await tpAccessReviewCreate({ title: form.title, scope: form.scope, items: rows });
    setOpen(false); setForm({ title: "", scope: "", items: "" }); reload();
  };
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-sm text-slate-500">{items.length} access reviews</div>
        <button data-testid="tp-ar-new" onClick={() => setOpen(true)} className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-500 text-white text-sm font-semibold rounded-lg px-4 py-2 transition"><Plus size={14} /> New review</button>
      </div>
      {items.length === 0 ? <Empty text="No access reviews yet — schedule a review of who has access to production systems." /> : (
        <div className="space-y-4">
          {items.map((r) => (
            <div key={r.id} className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`tp-ar-${r.id.slice(0,6)}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold text-slate-900">{r.title}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{r.scope || "no scope"} · due {r.due_at ? new Date(r.due_at).toLocaleDateString() : "—"}</div>
                </div>
                <span className={`text-[10px] uppercase tracking-widest font-bold border rounded-full px-2 py-0.5 ${r.status === "approved" ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-amber-100 text-amber-800 border-amber-200"}`}>{r.status}</span>
              </div>
              <div className="mt-3 space-y-1">
                {(r.items || []).map((it) => (
                  <div key={it.id} className="flex items-center gap-3 text-sm border-t border-slate-100 pt-2 first:border-t-0 first:pt-0">
                    <div className="flex-1">
                      <span className="font-semibold text-slate-800">{it.user}</span>
                      <span className="text-xs text-slate-500 ml-2">{it.role}</span>
                    </div>
                    <select disabled={r.status === "approved"} value={it.decision} onChange={async (e) => { await tpAccessReviewDecide(r.id, it.id, { decision: e.target.value }); reload(); }} className="text-xs border border-slate-300 rounded px-2 py-1 disabled:opacity-50">
                      <option value="pending">pending</option>
                      <option value="keep">keep</option>
                      <option value="revoke">revoke</option>
                    </select>
                  </div>
                ))}
              </div>
              {r.status !== "approved" && (
                <button data-testid={`tp-ar-complete-${r.id.slice(0,6)}`} onClick={async () => { try { await tpAccessReviewComplete(r.id); reload(); } catch (e) { alert(e?.response?.data?.detail || "Complete failed"); } }} className="mt-3 inline-flex items-center gap-1 text-xs px-3 py-1 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white font-semibold">
                  <Check size={12} /> Complete review
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      {open && (
        <Modal onClose={() => setOpen(false)} title="New access review">
          <div className="space-y-3">
            <input data-testid="tp-ar-title" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" placeholder="Title (e.g. Q3 prod Mongo access)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <input className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" placeholder="Scope (system / resource)" value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })} />
            <textarea className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm min-h-[120px]" placeholder="One person per line as `email, role`" value={form.items} onChange={(e) => setForm({ ...form, items: e.target.value })} />
            <button data-testid="tp-ar-create" onClick={create} disabled={!form.title} className="w-full bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white font-semibold rounded-lg px-4 py-2.5">Start review</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------- Modal */
function Modal({ title, onClose, children }) {
  return (
    <div role="dialog" className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-slate-900">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900"><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
